// src/services/pollingService.js
//
// The complete replacement for socket.io. `services/websocket.js` is
// deleted in this phase -- nothing should import it any more.
//
// Design
// ------
// One timer, not one per subscriber. Components subscribe to a topic and
// the service polls only the topics that currently have listeners, so an
// auction page doesn't keep hitting the burner endpoint after you leave it.
//
// Three things the old socket setup got wrong that this fixes:
//
//   * It used bare `fetch('/api/...')`, which only works when the API is
//     same-origin. This uses the configured axios instance, so it follows
//     REACT_APP_API_URL like every other call.
//   * It polled on a fixed 5s interval regardless of tab visibility. This
//     backs off to 60s when the tab is hidden and resumes immediately on
//     focus -- worth doing when the audience is on mobile data.
//   * A failing poll retried at full speed forever. This backs off on
//     consecutive errors and recovers on the first success.
//
// Emitting is gone. It was always one-directional in practice: the old
// `emit('new_bid')` fired alongside the REST call that actually placed the
// bid, so dropping it changes nothing except the console noise.

import api from './api';

const INTERVALS = {
  burner: 8000,        // announcements -- the one people notice
  notifications: 20000,
  auction: 5000,       // live bid updates while viewing an auction
};

const HIDDEN_INTERVAL = 60000;
const MAX_BACKOFF = 120000;

class PollingService {
  constructor() {
    this.listeners = new Map();   // event name -> [callback]
    this.timers = new Map();      // topic -> interval id
    this.state = new Map();       // topic -> last seen marker
    this.errorCounts = new Map(); // topic -> consecutive failures
    this.activeAuctionId = null;
    this.started = false;
    this.userId = null;

    this._onVisibilityChange = this._onVisibilityChange.bind(this);
  }

  // ---------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------

  connect(userId) {
    if (this.started) return;
    this.started = true;
    this.userId = userId;

    document.addEventListener('visibilitychange', this._onVisibilityChange);

    this._startTopic('burner');
    this._startTopic('notifications');
  }

  disconnect() {
    this.started = false;
    this.userId = null;
    this.activeAuctionId = null;

    document.removeEventListener('visibilitychange', this._onVisibilityChange);

    Array.from(this.timers.values()).forEach((id) => clearInterval(id));
    this.timers.clear();
    this.state.clear();
    this.errorCounts.clear();
  }

  _onVisibilityChange() {
    if (!this.started) return;

    // Snapshot the keys BEFORE touching the Map.
    //
    // This previously iterated this.timers directly with forEach while
    // deleting and re-adding each key inside the callback. Re-adding a
    // deleted key appends it to the end of the Map's insertion order, and
    // Map.forEach visits entries added during iteration -- so the same
    // topic was visited forever and the browser main thread locked up.
    // That is what produced the "page unresponsive / wait or exit" dialog
    // on returning to a tab.
    //
    // Array.from() detaches the key list from the live Map, so mutating
    // it below is safe.
    const topics = Array.from(this.timers.keys());

    if (document.visibilityState === 'visible') {
      // Catch up immediately, then resume normal cadence.
      topics.forEach((topic) => {
        const id = this.timers.get(topic);
        if (id) clearInterval(id);
        this.timers.delete(topic);
        this._startTopic(topic, { immediate: true });
      });
    } else {
      topics.forEach((topic) => {
        const id = this.timers.get(topic);
        if (id) clearInterval(id);
        this.timers.set(
          topic,
          setInterval(() => this._poll(topic), HIDDEN_INTERVAL)
        );
      });
    }
  }

  _startTopic(topic, { immediate = true } = {}) {
    if (this.timers.has(topic)) return;

    const base = INTERVALS[topic.startsWith('auction') ? 'auction' : topic] || 15000;
    if (immediate) this._poll(topic);
    this.timers.set(topic, setInterval(() => this._poll(topic), base));
  }

  _stopTopic(topic) {
    const id = this.timers.get(topic);
    if (id) clearInterval(id);
    this.timers.delete(topic);
    this.state.delete(topic);
    this.errorCounts.delete(topic);
  }

  _backOff(topic) {
    const failures = (this.errorCounts.get(topic) || 0) + 1;
    this.errorCounts.set(topic, failures);

    if (failures < 3) return;

    const base = INTERVALS[topic.startsWith('auction') ? 'auction' : topic] || 15000;
    const delay = Math.min(base * 2 ** (failures - 2), MAX_BACKOFF);

    const id = this.timers.get(topic);
    if (id) clearInterval(id);
    this.timers.set(topic, setInterval(() => this._poll(topic), delay));
  }

  _recovered(topic) {
    if (!this.errorCounts.get(topic)) return;
    this.errorCounts.set(topic, 0);
    // Safe: not inside an iteration over this.timers.
    const id = this.timers.get(topic);
    if (id) clearInterval(id);
    this.timers.delete(topic);
    this._startTopic(topic, { immediate: false });
  }

  // ---------------------------------------------------------------
  // Polling
  // ---------------------------------------------------------------

  async _poll(topic) {
    if (!localStorage.getItem('access_token')) return;

    try {
      if (topic === 'burner') await this._pollBurner();
      else if (topic === 'notifications') await this._pollNotifications();
      else if (topic.startsWith('auction:')) {
        await this._pollAuction(Number(topic.split(':')[1]));
      }
      this._recovered(topic);
    } catch (error) {
      // 401 means the token died -- stop rather than hammering the API.
      if (error?.response?.status === 401) {
        this.disconnect();
        return;
      }
      this._backOff(topic);
    }
  }

  async _pollBurner() {
    const { data } = await api.get('/admin/burner/messages');
    const messages = data?.messages || [];
    if (!messages.length) return;

    const newest = messages[0];
    const lastSeen = this.state.get('burner');

    if (lastSeen !== newest.id) {
      this.state.set('burner', newest.id);
      // Skip the very first poll after connect -- otherwise every page
      // load replays the last announcement as if it were new.
      if (lastSeen !== undefined) {
        this._fire('new_burner_message', newest);
      }
    }

    this._fire('burner_messages', messages);
  }

  async _pollNotifications() {
    const { data } = await api.get('/notifications/', { params: { limit: 20 } });
    const notifications = data?.notifications || [];
    const unread = data?.unread_count ?? notifications.filter((n) => !n.is_read).length;

    const previous = this.state.get('notifications');
    this.state.set('notifications', unread);

    this._fire('notifications', { notifications, unread_count: unread });

    if (previous !== undefined && unread > previous) {
      const newest = notifications.find((n) => !n.is_read);
      if (newest) this._fire('notification', newest);
    }
  }

  async _pollAuction(auctionId) {
    if (!auctionId) return;

    const { data } = await api.get(`/auctions/${auctionId}`);
    const auction = data?.auction || data;
    if (!auction) return;

    const marker = `${auction.current_price}:${auction.status}`;
    const previous = this.state.get(`auction:${auctionId}`);
    this.state.set(`auction:${auctionId}`, marker);

    if (previous !== undefined && previous !== marker) {
      const payload = {
        auction_id: auctionId,
        current_price: auction.current_price,
        status: auction.status,
        bid: auction.highest_bid || null,
      };
      // Fired under BOTH names: AuctionDetail listens for 'new_bid'
      // (the old socket event), newer code uses 'bid_update'.
      this._fire('bid_update', payload);
      this._fire('new_bid', payload);
    }

    this._fire('auction_update', auction);
  }

  // ---------------------------------------------------------------
  // Auction rooms (the old join_auction / leave_auction)
  // ---------------------------------------------------------------

  joinAuction(auctionId) {
    if (!auctionId) return;
    if (this.activeAuctionId === auctionId) return;

    if (this.activeAuctionId) this.leaveAuction(this.activeAuctionId);

    this.activeAuctionId = auctionId;
    this._startTopic(`auction:${auctionId}`);
  }

  leaveAuction(auctionId) {
    const id = auctionId || this.activeAuctionId;
    if (!id) return;
    this._stopTopic(`auction:${id}`);
    if (this.activeAuctionId === id) this.activeAuctionId = null;
  }

  // ---------------------------------------------------------------
  // Subscription API -- same shape as the old socket service, so
  // components migrate with an import change and nothing else.
  // ---------------------------------------------------------------

  on(event, callback) {
    if (!this.listeners.has(event)) this.listeners.set(event, []);
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    const remaining = this.listeners.get(event).filter((cb) => cb !== callback);
    if (remaining.length) this.listeners.set(event, remaining);
    else this.listeners.delete(event);
  }

  _fire(event, payload) {
    (this.listeners.get(event) || []).forEach((cb) => {
      try {
        cb(payload);
      } catch (error) {
        console.error(`[polling] listener for '${event}' threw:`, error);
      }
    });
  }

  /**
   * No-op. Kept so the old `emit(...)` call sites don't crash during the
   * migration -- polling is read-only. Any call here is a leftover and
   * should be deleted.
   */
  emit(event) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[polling] emit('${event}') ignored -- polling is one-directional. ` +
        'Remove this call.'
      );
    }
  }

  /** Force an immediate refresh, e.g. right after posting an announcement. */
  refresh(topic = 'burner') {
    this._poll(topic);
  }

  // Legacy aliases from the socket service.
  subscribeToBurner() {}
  unsubscribeFromBurner() {}
  onBurnerMessage(callback) {
    this.on('new_burner_message', callback);
  }

  /** Always true -- there is no connection to lose. */
  get isConnected() {
    return this.started;
  }
}

export const pollingService = new PollingService();
export default pollingService;
